import React from 'react';
import {
  AgnesButton,
  AgnesMark,
  AgnesInput,
  AgnesTextarea,
  AgnesSelect,
  AgnesSelectTrigger,
  AgnesSelectValue,
  AgnesSelectContent,
  AgnesSelectItem,
  AgnesBadge,
  AgnesCheckbox,
  AgnesTooltip,
  AgnesStepper,
  AgnesRadioCardGroup,
  AgnesRadioCard,
} from '@agnes/ui';
import { ArrowLeft, ArrowRight, Check, Search, Info as InfoIcon, CircleAlert } from 'lucide-react';
import { useCboStore } from '@/stores/useCboStore';
import { notify } from '@/stores/useToast';
import { confirm } from '@/stores/useConfirm';
import { useDirty } from '@/hooks/useDirty';
import {
  STEP_LABEL,
  STATUS_LABEL,
  CLASSIFICATION_LABEL,
  RISK_LABEL,
  SITUACAO_CONTRATUAL_LABEL,
  PCD_LABEL,
  GENERO_LABEL,
  TIPO_ADMISSAO_LABEL,
} from '@/lib/cbo';
import UtilsMask from '@/utils/UtilsMask';
import UtilsDate from '@/utils/UtilsDate';
import { AUTO_STATUS_LABEL, type CboAnaliseForm } from '@/lib/cboAnalise';
import ServiceCBO from '@/services/ServiceCBO';

type Any = any;

interface OccurrenceOption {
  value: string;
  label: string;
  diagnostic?: string;
  risk?: string;
}
interface OccupationOption {
  code: string;
  title: string;
}

// Autocomplete server-side de ocupação (paridade com o legado: busca por título
// em /cbo/list, não input de código). Seleção define o código recomendado.
function OccupationSearch({
  code,
  title,
  disabled,
  placeholderCode,
  placeholderTitle,
  dataQa,
  onSelect,
}: {
  code: string;
  title?: string;
  disabled?: boolean;
  placeholderCode?: string;
  placeholderTitle?: string;
  dataQa?: string;
  onSelect: (code: string, title: string) => void;
}) {
  const [term, setTerm] = React.useState('');
  const [aberto, setAberto] = React.useState(false);
  const [resultados, setResultados] = React.useState<OccupationOption[]>([]);
  const [buscando, setBuscando] = React.useState(false);
  const timer = React.useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const ativo = React.useRef(true);

  // Cancela o debounce pendente e bloqueia setState após desmontar (fecha a tela
  // no meio da busca). Reafirma `ativo` no setup para sobreviver ao double-invoke do
  // StrictMode (mount→cleanup→mount), que senão deixaria `ativo` preso em false e
  // abortaria toda busca antes de abrir o dropdown.
  React.useEffect(() => {
    ativo.current = true;
    return () => {
      ativo.current = false;
      if (timer.current) clearTimeout(timer.current);
    };
  }, []);

  const selecionado = code ? `${code}${title ? ` - ${title}` : ''}` : '';
  const sugestao =
    !code && placeholderCode
      ? `${placeholderCode}${placeholderTitle ? ` - ${placeholderTitle}` : ''}`
      : '';

  const buscar = (q: string) => {
    setTerm(q);
    if (timer.current) clearTimeout(timer.current);
    const t = q.trim();
    if (!t) {
      setResultados([]);
      setAberto(false);
      return;
    }
    timer.current = setTimeout(async () => {
      setBuscando(true);
      try {
        const { data } = await ServiceCBO.searchOccupations({ title: t });
        if (!ativo.current) return;
        setResultados(
          (data?.content ?? []).map((o: Any) => ({ code: String(o.code), title: o.title ?? '' })),
        );
        setAberto(true);
      } catch {
        if (ativo.current) setResultados([]);
      } finally {
        if (ativo.current) setBuscando(false);
      }
    }, 350);
  };

  const escolher = (o: OccupationOption) => {
    onSelect(o.code, o.title);
    setTerm('');
    setResultados([]);
    setAberto(false);
  };

  const qa = (sufixo: string) => (dataQa ? `${dataQa}-${sufixo}` : undefined);

  return (
    <div className="relative">
      <AgnesInput
        value={term}
        onChange={(e) => buscar(e.target.value)}
        onFocus={() => {
          if (resultados.length) setAberto(true);
        }}
        onBlur={() => setTimeout(() => setAberto(false), 150)}
        placeholder={selecionado || sugestao || 'Buscar ocupação por título…'}
        startIcon={<Search className="h-4 w-4" />}
        disabled={disabled}
        dataQa={dataQa}
      />
      {selecionado && !term && (
        <p className="mt-1 text-xs text-agnes-text-muted" data-qa={qa('selecionada')}>
          Selecionada: {selecionado}
        </p>
      )}
      {aberto && (
        <ul
          className="absolute z-20 mt-1 max-h-60 w-full overflow-auto rounded-lg border border-agnes-border bg-agnes-bg-default shadow-lg"
          data-qa={qa('resultados')}
        >
          {buscando && (
            <li className="px-3 py-2 text-sm text-agnes-text-muted" data-qa={qa('buscando')}>
              Buscando…
            </li>
          )}
          {!buscando && resultados.length === 0 && (
            <li className="px-3 py-2 text-sm text-agnes-text-muted" data-qa={qa('sem-resultados')}>
              Nenhuma ocupação encontrada.
            </li>
          )}
          {resultados.map((o) => (
            <li key={o.code}>
              <button
                type="button"
                data-qa={qa(`resultado-${o.code}`)}
                onMouseDown={(e) => {
                  e.preventDefault();
                  escolher(o);
                }}
                className="block w-full px-3 py-2 text-left text-sm hover:bg-agnes-bg-muted"
              >
                <span className="font-medium">{o.code}</span> - {o.title}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

const FORM_VAZIO: CboAnaliseForm = {
  occupationCode: '',
  occurrenceId: '',
  step: 'ANALISAR',
  workDescription: '',
  recommendations: '',
};

const STEP_OPTIONS = Object.entries(STEP_LABEL).map(([value, label]) => ({ value, label }));

const ABAS = ['trabalhador', 'ocupacao', 'analise'] as const;
type Aba = (typeof ABAS)[number];
const ETAPAS = [
  { label: 'Dados do trabalhador' },
  { label: 'Dados da ocupação' },
  { label: 'Análise Agnes' },
];

function Info({
  label,
  value,
  dica,
  dataQa,
}: {
  label: string;
  value?: React.ReactNode;
  dica?: string | null;
  dataQa?: string;
}) {
  return (
    <div data-qa={dataQa}>
      <p
        className="flex items-center gap-1 text-xs font-medium text-agnes-text-muted"
        data-qa={dataQa ? `${dataQa}-label` : undefined}
      >
        {label}
        {dica && (
          <AgnesTooltip
            content={dica}
            className="max-w-sm whitespace-normal"
            dataQa={dataQa ? `${dataQa}-dica-conteudo` : undefined}
          >
            <InfoIcon
              className="h-3.5 w-3.5"
              aria-label={`Descrição sumária: ${label}`}
              data-qa={dataQa ? `${dataQa}-dica` : undefined}
            />
          </AgnesTooltip>
        )}
      </p>
      <p className="text-sm text-agnes-text" data-qa={dataQa ? `${dataQa}-valor` : undefined}>
        {value || '-'}
      </p>
    </div>
  );
}

// rótulo PT-BR de um enum, com fallback pro valor bruto
const labelOf = (map: Record<string, string>, v?: string) => (v ? (map[v] ?? v) : undefined);

const dataBR = (v?: string | null, comHora = false) =>
  v ? UtilsDate.formatBR(v, { comHora }) : undefined;

// badge de validação automática (analiseAutomatica)
function AutoBadge({ label, status, dataQa }: { label: string; status?: string; dataQa?: string }) {
  if (!status) return null;
  const ok = status === 'SEM_DIVERGENCIA';
  return (
    <div className="flex items-center gap-2" data-qa={dataQa}>
      <span
        className="text-xs text-agnes-text-muted"
        data-qa={dataQa ? `${dataQa}-label` : undefined}
      >
        {label}:
      </span>
      <AgnesBadge
        variant={ok ? 'success' : 'error'}
        dataQa={dataQa ? `${dataQa}-valor` : undefined}
      >
        {AUTO_STATUS_LABEL[status] ?? status}
      </AgnesBadge>
    </div>
  );
}

// rótulo verde "Recomendações Agnes" exibido sob os campos da aba de análise
function RecomendacaoAgnes({ dataQa }: { dataQa: string }) {
  return (
    <div className="mt-1 flex items-center gap-1 text-xs text-agnes-primary" data-qa={dataQa}>
      <AgnesMark size={14} title="" />
      <span>Recomendações Agnes</span>
    </div>
  );
}

export default function FormCboAnalise({
  onClose,
  onDirtyChange,
}: {
  onClose: () => void;
  onDirtyChange?: (dirty: boolean) => void;
}) {
  const analise = useCboStore((s) => s.analise);
  const carregandoAnalise = useCboStore((s) => s.carregandoAnalise);
  const salvarAnalise = useCboStore((s) => s.salvarAnalise);
  const sugerirOcupacaoPorDescricao = useCboStore((s) => s.sugerirOcupacaoPorDescricao);

  const [form, setForm] = React.useState<CboAnaliseForm>(FORM_VAZIO);
  // Salvar desabilitado até haver alteração. Como o form parte de FORM_VAZIO, o baseline
  // é fixado com o dado carregado via rebase() no useEffect de inicialização.
  const { isDirty, rebase } = useDirty(form);

  React.useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);
  const [occTitle, setOccTitle] = React.useState('');
  const [salvando, setSalvando] = React.useState(false);
  const [ocorrencias, setOcorrencias] = React.useState<OccurrenceOption[]>([]);
  const [usarOcupacaoCliente, setUsarOcupacaoCliente] = React.useState(false);
  const [usarAtividadesCliente, setUsarAtividadesCliente] = React.useState(false);
  const [aba, setAba] = React.useState<Aba>('trabalhador');
  const [erroOcupacao, setErroOcupacao] = React.useState('');
  // Marca que a ocupação exibida veio de uma sugestão Agnes (recomendação explícita =
  // Camada 1, ou derivada da descrição do cliente = Camada 2), p/ rotular.
  const [sugestaoOcupacao, setSugestaoOcupacao] = React.useState(false);
  // Trava a Camada 2 (assíncrona): se o usuário já mexeu na ocupação, não sobrescreve.
  const ocupacaoTocada = React.useRef(false);

  const rec = analise?.recomendacoesAgnes ?? {};
  const auto = analise?.analiseAutomatica ?? {};
  const trab = analise?.trabalhador ?? {};
  const desligado = String(trab.situacao ?? '').toUpperCase() === 'TERMINATED';
  const ocorrenciaSelecionada = ocorrencias.find((o) => o.value === form.occurrenceId);
  const diagnostico = ocorrenciaSelecionada?.diagnostic ?? rec.occurrence?.diagnostic;
  const grauRisco = ocorrenciaSelecionada?.risk ?? rec.occurrence?.risk;
  const ocupacaoAtual = analise?.cboInfoId
    ? `${analise.cboInfoId} - ${analise.cboInfoTitle ?? ''}`
    : undefined;

  // inicializa o form a partir dos dados carregados
  React.useEffect(() => {
    if (!analise) return;

    // Pré-seleção da ocupação: só pré-seleciona "Nova ocupação" quando a recomendação
    // Agnes DIVERGE da ocupação já cadastrada pelo cliente. Sem divergência (recomendação
    // igual à do cliente, ou ausente), mantém o dado do cliente em "Ocupação existente".
    const clientCode = analise.cboInfoId ?? '';
    const recCode = rec.occupation?.code ?? '';
    const recDiverge = !!recCode && recCode !== clientCode;

    let occCode = '';
    let occTit = '';
    let usarCliente = false;
    let sugest = false;
    if (recDiverge) {
      occCode = recCode;
      occTit = rec.occupation?.title ?? '';
      sugest = true; // recomendação divergente = sugestão Agnes
    } else if (clientCode) {
      occCode = clientCode;
      occTit = analise.cboInfoTitle ?? '';
      usarCliente = true; // mantém a ocupação do cliente
    }

    const inicial: CboAnaliseForm = {
      occupationCode: occCode,
      occurrenceId: rec.occurrence?.id != null ? String(rec.occurrence.id) : '',
      step: rec.step ?? 'ANALISAR',
      workDescription: rec.workDescription ?? '',
      recommendations: rec.recommendations ?? '',
    };
    setForm(inicial);
    rebase(inicial); // baseline = dado carregado (form partiu de FORM_VAZIO)
    setOccTitle(occTit);
    setUsarOcupacaoCliente(usarCliente);
    setUsarAtividadesCliente(false);
    setAba('trabalhador');
    setErroOcupacao('');
    ocupacaoTocada.current = false;
    setSugestaoOcupacao(sugest);

    // Camada 2: sem recomendação explícita, mas com descrição das atividades do cliente,
    // pedimos uma sugestão por atividade. Só troca para "Nova ocupação"
    // quando a sugestão DIVERGE da ocupação do cliente; igual (ou sem sugestão) mantém o
    // dado do cliente. Não sobrescreve o que o usuário já tiver tocado; falha é silenciosa.
    let ativoSugestao = true;
    if (!recCode && analise.clientWorkDescription) {
      sugerirOcupacaoPorDescricao(analise.clientWorkDescription).then((sug) => {
        if (!ativoSugestao || !sug || ocupacaoTocada.current) return;
        if (sug.code === clientCode) return; // não diverge → mantém "Ocupação existente"
        setUsarOcupacaoCliente(false);
        setForm((s) => ({ ...s, occupationCode: sug.code }));
        setOccTitle(sug.title);
        setSugestaoOcupacao(true);
      });
    }
    return () => {
      ativoSugestao = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [analise]);

  // carrega as ocorrências (config de risco) para o select
  React.useEffect(() => {
    let ativo = true;
    ServiceCBO.listRiskOccurrences({})
      .then((r: Any) => {
        if (!ativo) return;
        const items = (r.data?.content ?? []).map((o: Any) => ({
          value: String(o.id),
          label: o.occurrence ?? String(o.id),
          diagnostic: o.diagnostic,
          risk: o.risk,
        }));
        setOcorrencias(items);
      })
      .catch(() => {
        if (ativo) setOcorrencias([]);
      });
    return () => {
      ativo = false;
    };
  }, []);

  const set = (k: keyof CboAnaliseForm) => (v: string) => setForm((s) => ({ ...s, [k]: v }));

  const ultimaAba = aba === ABAS[ABAS.length - 1];
  const ocupacaoVazia = !form.occupationCode.trim();
  const avancar = () => {
    if (aba === 'ocupacao' && ocupacaoVazia) {
      setErroOcupacao('Informe a ocupação para avançar.');
      return;
    }
    const proxima = ABAS[ABAS.indexOf(aba) + 1];
    if (proxima) setAba(proxima);
  };

  // Clicar direto na etapa não pode pular a Ocupação (bypass do botão Avançar).
  const irParaAba = (destino: Aba) => {
    if (destino === 'analise' && ocupacaoVazia) {
      setErroOcupacao('Informe a ocupação para avançar.');
      setAba('ocupacao');
      return;
    }
    setAba(destino);
  };

  const submit = async () => {
    // Ocupação é obrigatória para salvar, independentemente da etapa.
    if (ocupacaoVazia) {
      setAba('ocupacao');
      setErroOcupacao('Informe a ocupação para avançar.');
      return;
    }
    // Espelha a validação do BFF: ANALISADO exige ocupação, ocorrência e descrição.
    if (
      form.step === 'ANALISADO' &&
      (!form.occupationCode.trim() || !form.occurrenceId || !form.workDescription.trim())
    ) {
      notify.error("Para a etapa 'Analisado', informe ocupação, ocorrência e descrição.");
      return;
    }
    setSalvando(true);
    try {
      await salvarAnalise(form);
      rebase();
      onClose();
    } catch {
      // erro já notificado no store
    } finally {
      setSalvando(false);
    }
  };

  // Avisa antes de fechar/cancelar se houver alterações não salvas.
  const fecharComAviso = async () => {
    if (
      isDirty &&
      !(await confirm({
        title: 'Alterações não salvas',
        message: 'Você tem alterações que não foram salvas e serão perdidas. Deseja sair?',
        okLabel: 'Sair',
        cancelLabel: 'Continuar editando',
      }))
    ) {
      return;
    }
    onClose();
  };

  // Rodapé em stepper: "Voltar" recua uma etapa; na primeira, sai para a listagem.
  const voltar = () => {
    const idx = ABAS.indexOf(aba);
    if (idx <= 0) {
      fecharComAviso();
      return;
    }
    setAba(ABAS[idx - 1]);
  };

  return (
    <div className="w-full" data-qa="cbo-analise-form">
      {carregandoAnalise ? (
        <p className="py-8 text-center text-agnes-text-muted" data-qa="cbo-analise-carregando">
          Carregando análise…
        </p>
      ) : (
        <div data-qa="cbo-analise-etapas-wrap">
          <AgnesStepper
            steps={ETAPAS}
            activeStep={ABAS.indexOf(aba)}
            onStepChange={(i) => irParaAba(ABAS[i])}
            isStepClickable={(i) => ABAS[i] !== 'analise' || !ocupacaoVazia}
            dataQa="cbo-analise-etapas"
          />

          {/* Step 1 — Dados do trabalhador */}
          {aba === 'trabalhador' && (
            <div className="flex flex-col gap-5 pt-4" data-qa="cbo-analise-etapa-trabalhador">
              <div data-qa="cbo-analise-trabalhador-identificacao">
                <p
                  className="text-lg font-semibold text-agnes-text"
                  data-qa="cbo-analise-trabalhador-nome"
                >
                  {trab.nome || '-'}
                </p>
                {trab.nomeSocial && (
                  <p
                    className="text-sm text-agnes-text-muted"
                    data-qa="cbo-analise-trabalhador-nome-social"
                  >
                    {trab.nomeSocial}
                  </p>
                )}
              </div>

              <div
                className="grid grid-cols-1 gap-4 rounded-lg border border-agnes-border p-3 sm:grid-cols-4"
                data-qa="cbo-analise-trabalhador-dados-principais"
              >
                <Info label="Cargo" value={trab.cargo} dataQa="cbo-analise-trabalhador-cargo" />
                <Info
                  label="Matrícula"
                  value={trab.matricula}
                  dataQa="cbo-analise-trabalhador-matricula"
                />
                <Info
                  label="CPF"
                  value={UtilsMask.cpf(trab.cpf)}
                  dataQa="cbo-analise-trabalhador-cpf"
                />
                <Info
                  label={trab.cnpj ? UtilsMask.cnpj(trab.cnpj) : 'Empresa'}
                  value={trab.empresa}
                  dataQa="cbo-analise-trabalhador-empresa"
                />
              </div>

              <div
                className="grid grid-cols-1 gap-4 rounded-lg border border-agnes-border p-3 sm:grid-cols-3"
                data-qa="cbo-analise-trabalhador-contrato"
              >
                <Info
                  label="Situação contratual"
                  value={
                    trab.situacao && (
                      <span className={desligado ? 'text-agnes-error' : 'text-agnes-success'}>
                        {labelOf(SITUACAO_CONTRATUAL_LABEL, String(trab.situacao).toUpperCase())}
                      </span>
                    )
                  }
                  dataQa="cbo-analise-trabalhador-situacao-contratual"
                />
                <Info
                  label="Tipo de admissão"
                  value={labelOf(TIPO_ADMISSAO_LABEL, trab.tipoAdmissao)}
                  dataQa="cbo-analise-trabalhador-tipo-admissao"
                />
                <Info
                  label="Motivo de encerramento"
                  value={trab.motivoEncerramento}
                  dataQa="cbo-analise-trabalhador-motivo-encerramento"
                />
                <Info
                  label="Início"
                  value={dataBR(trab.dataAdmissao)}
                  dataQa="cbo-analise-trabalhador-inicio"
                />
                <Info
                  label="Fim"
                  value={
                    desligado
                      ? dataBR(trab.dataDesligamento)
                      : trab.dataAdmissao
                        ? 'Em andamento'
                        : undefined
                  }
                  dataQa="cbo-analise-trabalhador-fim"
                />
                <Info
                  label="Gênero"
                  value={labelOf(GENERO_LABEL, trab.genero)}
                  dataQa="cbo-analise-trabalhador-genero"
                />
                <Info
                  label="PCD"
                  value={trab.pcd == null ? undefined : PCD_LABEL[String(trab.pcd)]}
                  dataQa="cbo-analise-trabalhador-pcd"
                />
              </div>

              <div
                className="grid grid-cols-1 gap-4 rounded-lg border border-agnes-border p-3"
                data-qa="cbo-analise-trabalhador-enquadramento"
              >
                <Info
                  label="Categoria"
                  value={trab.categoria}
                  dataQa="cbo-analise-trabalhador-categoria"
                />
                <Info
                  label="Regime previdenciário"
                  value={trab.regimePrevidenciario}
                  dataQa="cbo-analise-trabalhador-regime-previdenciario"
                />
              </div>
            </div>
          )}

          {/* Step 2 — Dados da ocupação (evento S-2240 + ocupação atual × recomendada) */}
          {aba === 'ocupacao' && (
            <div className="flex flex-col gap-5 pt-4" data-qa="cbo-analise-etapa-ocupacao">
              {/* Dados do evento S-2240 */}
              <div
                className="grid grid-cols-1 gap-4 rounded-lg border border-agnes-border p-3 sm:grid-cols-3"
                data-qa="cbo-analise-evento"
              >
                <div className="break-all sm:col-span-2">
                  <Info
                    label="ID (S-2240)"
                    value={analise?.idDocumentoEvtExpRisco}
                    dataQa="cbo-analise-evento-id"
                  />
                </div>
                <Info
                  label="Versão eSocial"
                  value={analise?.esocialVersion}
                  dataQa="cbo-analise-evento-versao"
                />
                <Info
                  label="Envio do evento"
                  value={dataBR(analise?.dataEnvioEvtExpRisco, true)}
                  dataQa="cbo-analise-evento-envio"
                />
                <Info
                  label="Início (S-2240)"
                  value={dataBR(analise?.startExposition)}
                  dataQa="cbo-analise-evento-inicio"
                />
                <Info
                  label="Fim (S-2240)"
                  value={dataBR(analise?.endExposition)}
                  dataQa="cbo-analise-evento-fim"
                />
                <Info
                  label="Ambiente de trabalho (S-2240)"
                  value={analise?.ambienteS2240}
                  dataQa="cbo-analise-evento-ambiente-trabalho"
                />
                <Info
                  label="Local de trabalho (S-2200/S-2206)"
                  value={analise?.ambienteS2200S2206}
                  dataQa="cbo-analise-evento-local-trabalho"
                />
                <Info label="Setor" value={analise?.department} dataQa="cbo-analise-evento-setor" />
                <Info label="Cargo" value={trab.cargo} dataQa="cbo-analise-ocupacao-cargo" />
              </div>

              {/* Definição da ocupação em radio-cards: "Ocupação existente" (dados do
                    cliente) × "Nova ocupação" (recomendada). Substitui o antigo checkbox
                    "Usar dados do cliente" da ocupação. */}
              <AgnesRadioCardGroup
                label="Definição da ocupação"
                direction="horizontal"
                value={usarOcupacaoCliente ? 'existente' : 'nova'}
                onValueChange={(v) => {
                  ocupacaoTocada.current = true;
                  if (v === 'existente') {
                    setUsarOcupacaoCliente(true);
                    setSugestaoOcupacao(false);
                    set('occupationCode')(analise?.cboInfoId ?? '');
                    setOccTitle(analise?.cboInfoTitle ?? '');
                    if (analise?.cboInfoId) setErroOcupacao('');
                  } else {
                    // ao VIR de "Existente", limpa para o analista escolher uma nova
                    // ocupação do zero (não herda o código do cliente na busca).
                    if (usarOcupacaoCliente) {
                      set('occupationCode')('');
                      setOccTitle('');
                      setSugestaoOcupacao(false);
                    }
                    setUsarOcupacaoCliente(false);
                  }
                }}
                dataQa="cbo-analise-ocupacao-definicao"
              >
                <AgnesRadioCard
                  value="existente"
                  label={
                    <span className="flex w-full items-center justify-between gap-2">
                      Ocupação existente
                      <AgnesBadge>Dados do cliente</AgnesBadge>
                    </span>
                  }
                  dataQa="cbo-analise-ocupacao-existente"
                >
                  {analise?.cboInfoId ? (
                    <div className="flex flex-col gap-1">
                      <div className="flex items-center gap-1">
                        <span
                          className="text-sm font-semibold text-agnes-text"
                          data-qa="cbo-analise-ocupacao-existente-codigo"
                        >
                          {analise.cboInfoId}
                        </span>
                        {analise?.cboInfoSummaryDescription && (
                          <AgnesTooltip
                            content={analise.cboInfoSummaryDescription}
                            className="max-w-sm whitespace-normal"
                            dataQa="cbo-analise-ocupacao-existente-dica-conteudo"
                          >
                            <InfoIcon
                              className="h-3.5 w-3.5 text-agnes-text-muted"
                              aria-label="Descrição sumária (cliente)"
                              data-qa="cbo-analise-ocupacao-existente-dica"
                            />
                          </AgnesTooltip>
                        )}
                      </div>
                      <span
                        className="text-sm text-agnes-text"
                        data-qa="cbo-analise-ocupacao-existente-titulo"
                      >
                        {analise.cboInfoTitle ?? '-'}
                      </span>
                      <p className="mt-2 text-xs text-agnes-text-muted">
                        Selecione esta opção para manter a ocupação cadastrada pelo cliente.
                      </p>
                      <AutoBadge
                        label="Ocupação"
                        status={auto.statusOcupacao}
                        dataQa="cbo-analise-tag-ocupacao"
                      />
                    </div>
                  ) : (
                    <p className="text-xs text-agnes-text-muted">
                      O cliente não informou ocupação.
                    </p>
                  )}
                </AgnesRadioCard>

                <AgnesRadioCard
                  value="nova"
                  label={
                    <span className="flex w-full items-center justify-between gap-2">
                      Nova ocupação
                      <AgnesBadge>Recomendada</AgnesBadge>
                    </span>
                  }
                  dataQa="cbo-analise-ocupacao-nova"
                >
                  <div data-qa="cbo-analise-ocupacao-recomendada">
                    <div className="mb-1 flex items-center gap-1">
                      <label
                        className="block text-sm font-medium text-agnes-text"
                        data-qa="cbo-analise-ocupacao-recomendada-label"
                      >
                        Selecione uma ocupação
                      </label>
                      {rec.occupation?.summaryDescription && (
                        <AgnesTooltip
                          content={rec.occupation.summaryDescription}
                          className="max-w-sm whitespace-normal"
                          dataQa="cbo-analise-ocupacao-recomendada-dica-conteudo"
                        >
                          <InfoIcon
                            className="h-3.5 w-3.5 text-agnes-text-muted"
                            aria-label="Descrição sumária (recomendada)"
                            data-qa="cbo-analise-ocupacao-recomendada-dica"
                          />
                        </AgnesTooltip>
                      )}
                    </div>
                    <OccupationSearch
                      code={usarOcupacaoCliente ? '' : form.occupationCode}
                      title={usarOcupacaoCliente ? '' : occTitle}
                      disabled={usarOcupacaoCliente}
                      placeholderCode={analise?.cboInfoId}
                      placeholderTitle={analise?.cboInfoTitle}
                      dataQa="cbo-analise-ocupacao-recomendada-busca"
                      onSelect={(code, title) => {
                        ocupacaoTocada.current = true;
                        setSugestaoOcupacao(false);
                        set('occupationCode')(code);
                        setOccTitle(title);
                        if (code.trim()) setErroOcupacao('');
                      }}
                    />
                    {/* Rotula a ocupação quando veio de sugestão Agnes (Camada 1 =
                          recomendação explícita; Camada 2 = derivada da descrição do
                          cliente). Some ao escolher "Ocupação existente". */}
                    {sugestaoOcupacao && form.occupationCode.trim() && !usarOcupacaoCliente && (
                      <div
                        className="mt-1 flex items-center gap-1 text-xs text-agnes-primary"
                        data-qa="cbo-analise-ocupacao-sugestao-agnes"
                      >
                        <AgnesMark size={14} title="" />
                        <span>Sugestão Agnes</span>
                      </div>
                    )}
                    {erroOcupacao && (
                      <p
                        className="mt-1 flex items-center gap-1.5 text-xs text-agnes-error"
                        data-qa="cbo-analise-ocupacao-recomendada-erro"
                      >
                        <CircleAlert className="h-3.5 w-3.5 shrink-0" /> {erroOcupacao}
                      </p>
                    )}
                    <p className="mt-2 text-xs text-agnes-text-muted">
                      Esta ocupação será usada na análise e nas próximas etapas.
                    </p>
                  </div>
                </AgnesRadioCard>
              </AgnesRadioCardGroup>

              <div data-qa="cbo-analise-atividades">
                <label
                  className="mb-1 block text-sm font-medium text-agnes-text"
                  data-qa="cbo-analise-atividades-label"
                >
                  Descrição das atividades (evento S-2240)
                </label>
                <div className="mb-2 flex items-center gap-2">
                  <AgnesCheckbox
                    label="Usar dados do cliente"
                    checked={usarAtividadesCliente}
                    onCheckedChange={(checked) => {
                      const v = checked === true;
                      setUsarAtividadesCliente(v);
                      if (v) set('workDescription')(analise?.clientWorkDescription ?? '');
                    }}
                    dataQa="cbo-analise-usar-atividades-cliente"
                  />
                  <AgnesTooltip
                    content="Preenche automaticamente este bloco com as informações iguais as do cliente."
                    className="max-w-sm whitespace-normal"
                    dataQa="cbo-analise-usar-atividades-cliente-dica-conteudo"
                  >
                    <InfoIcon
                      className="h-3.5 w-3.5 text-agnes-text-muted"
                      data-qa="cbo-analise-usar-atividades-cliente-dica"
                    />
                  </AgnesTooltip>
                </div>
                <AgnesTextarea
                  value={form.workDescription}
                  onChange={(e) => set('workDescription')(e.target.value)}
                  rows={3}
                  maxLength={999}
                  disabled={usarAtividadesCliente}
                  dataQa="cbo-analise-descricao-atividades"
                  placeholder={
                    analise?.clientWorkDescription || 'Descreva as atividades do trabalhador…'
                  }
                />
              </div>

              {/* Grau de instrução × escolaridade mínima / órgão de classe atual × recomendado */}
              <div
                className="grid grid-cols-1 gap-4 rounded-lg border border-agnes-border p-3 sm:grid-cols-2"
                data-qa="cbo-analise-formacao"
              >
                <div className="flex flex-col gap-2">
                  <Info
                    label="Grau de instrução"
                    value={analise?.educationLevel}
                    dataQa="cbo-analise-grau-instrucao"
                  />
                  <AutoBadge
                    label="Grau de instrução"
                    status={auto.statusGrauInstrucao}
                    dataQa="cbo-analise-tag-grau-instrucao"
                  />
                </div>
                <Info
                  label="Escolaridade mínima (recomendada)"
                  value={rec.occupation?.education?.description}
                  dataQa="cbo-analise-escolaridade-minima-recomendada"
                />
                <div className="flex flex-col gap-2">
                  <Info
                    label="Órgão de classe"
                    value={analise?.cboInfoProfessionalRegulatoryBodyDescription}
                    dataQa="cbo-analise-orgao-classe"
                  />
                  <AutoBadge
                    label="Órgão de classe"
                    status={auto.statusOrgaoClasse}
                    dataQa="cbo-analise-tag-orgao-classe"
                  />
                </div>
                <Info
                  label="Órgão de classe (recomendado)"
                  value={rec.occupation?.professionalRegulatoryBody?.description}
                  dataQa="cbo-analise-orgao-classe-recomendado"
                />
              </div>
            </div>
          )}

          {/* Step 3 — Análise Agnes */}
          {aba === 'analise' && (
            <div className="flex flex-col gap-5 pt-4" data-qa="cbo-analise-etapa-analise">
              {/* Resumo no topo: leitura rápida da situação antes dos campos de análise.
                    As tags (Inválido / Divergente...) vêm de analise.analiseAutomatica. */}
              <div
                className="grid grid-cols-1 gap-4 rounded-lg border border-agnes-border p-3 sm:grid-cols-2"
                data-qa="cbo-analise-resumo"
              >
                <div className="flex flex-col gap-2">
                  <Info
                    label="Ocupação"
                    value={ocupacaoAtual}
                    dataQa="cbo-analise-resumo-ocupacao"
                  />
                  <AutoBadge
                    label="Ocupação"
                    status={auto.statusOcupacao}
                    dataQa="cbo-analise-resumo-tag-ocupacao"
                  />
                </div>
                <Info
                  label="Descrição das atividades (evento S-2240)"
                  value={analise?.clientWorkDescription}
                  dataQa="cbo-analise-resumo-descricao-atividades"
                />
                <div className="flex flex-col gap-2">
                  <Info
                    label="Grau de instrução"
                    value={analise?.educationLevel}
                    dataQa="cbo-analise-resumo-grau-instrucao"
                  />
                  <AutoBadge
                    label="Grau de instrução"
                    status={auto.statusGrauInstrucao}
                    dataQa="cbo-analise-resumo-tag-grau-instrucao"
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <Info
                    label="Órgão de classe"
                    value={analise?.cboInfoProfessionalRegulatoryBodyDescription}
                    dataQa="cbo-analise-resumo-orgao-classe"
                  />
                  <AutoBadge
                    label="Órgão de classe"
                    status={auto.statusOrgaoClasse}
                    dataQa="cbo-analise-resumo-tag-orgao-classe"
                  />
                </div>
              </div>

              {/* 1. Ocorrência */}
              <div data-qa="cbo-analise-ocorrencia-campo">
                <label
                  className="mb-1 block text-sm font-medium text-agnes-text"
                  data-qa="cbo-analise-ocorrencia-label"
                >
                  Ocorrência
                </label>
                <AgnesSelect value={form.occurrenceId} onValueChange={set('occurrenceId')}>
                  <AgnesSelectTrigger className="w-full" dataQa="cbo-analise-ocorrencia">
                    <AgnesSelectValue placeholder="Selecione a ocorrência" />
                  </AgnesSelectTrigger>
                  <AgnesSelectContent dataQa="cbo-analise-ocorrencia-lista">
                    {ocorrencias.map((o) => (
                      <AgnesSelectItem
                        key={o.value}
                        value={o.value}
                        dataQa="cbo-analise-ocorrencia"
                      >
                        {o.label}
                      </AgnesSelectItem>
                    ))}
                  </AgnesSelectContent>
                </AgnesSelect>
                <RecomendacaoAgnes dataQa="cbo-analise-ocorrencia-recomendacao" />
              </div>

              {/* 2. Diagnóstico (caixa grande, só leitura) */}
              <div data-qa="cbo-analise-diagnostico-campo">
                <div
                  className="min-h-32 rounded-lg bg-agnes-bg-muted p-4"
                  data-qa="cbo-analise-diagnostico"
                >
                  <p
                    className="text-xs font-medium text-agnes-text-muted"
                    data-qa="cbo-analise-diagnostico-label"
                  >
                    Diagnóstico
                  </p>
                  <p
                    className="mt-1 whitespace-pre-wrap text-sm text-agnes-text"
                    data-qa="cbo-analise-diagnostico-valor"
                  >
                    {diagnostico || '-'}
                  </p>
                </div>
                <RecomendacaoAgnes dataQa="cbo-analise-diagnostico-recomendacao" />
              </div>

              {/* 3. Grau de risco | Classificação de análise */}
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div data-qa="cbo-analise-grau-risco-campo">
                  <div className="rounded-lg bg-agnes-bg-muted p-3">
                    <Info
                      label="Grau de risco"
                      value={labelOf(RISK_LABEL, grauRisco)}
                      dataQa="cbo-analise-grau-risco"
                    />
                  </div>
                  <RecomendacaoAgnes dataQa="cbo-analise-grau-risco-recomendacao" />
                </div>
                <div className="self-start rounded-lg bg-agnes-bg-muted p-3">
                  <Info
                    label="Classificação de análise"
                    value={labelOf(CLASSIFICATION_LABEL, rec.classification)}
                    dataQa="cbo-analise-classificacao"
                  />
                </div>
              </div>

              {/* 4. Comentários e recomendações adicionais + contador */}
              <div data-qa="cbo-analise-comentarios-campo">
                <label
                  className="mb-1 block text-sm font-medium text-agnes-text"
                  data-qa="cbo-analise-comentarios-label"
                >
                  Comentários e recomendações adicionais
                </label>
                <AgnesTextarea
                  value={form.recommendations}
                  onChange={(e) => set('recommendations')(e.target.value)}
                  rows={5}
                  maxLength={255}
                  dataQa="cbo-analise-comentarios"
                  placeholder="Comentários e recomendações…"
                />
                <div className="flex items-start justify-between">
                  <RecomendacaoAgnes dataQa="cbo-analise-comentarios-recomendacao" />
                  <span
                    className="mt-1 text-xs text-agnes-text-muted"
                    data-qa="cbo-analise-comentarios-contador"
                  >
                    {form.recommendations.length} / 255
                  </span>
                </div>
              </div>

              {/* 5. Etapa de análise | Status de análise */}
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div data-qa="cbo-analise-etapa-campo">
                  <label
                    className="mb-1 block text-sm font-medium text-agnes-text"
                    data-qa="cbo-analise-etapa-label"
                  >
                    Etapa de análise *
                  </label>
                  <AgnesSelect value={form.step} onValueChange={set('step')}>
                    <AgnesSelectTrigger className="w-full" dataQa="cbo-analise-etapa">
                      <AgnesSelectValue placeholder="Selecione a etapa" />
                    </AgnesSelectTrigger>
                    <AgnesSelectContent dataQa="cbo-analise-etapa-lista">
                      {STEP_OPTIONS.map((o) => (
                        <AgnesSelectItem key={o.value} value={o.value} dataQa="cbo-analise-etapa">
                          {o.label}
                        </AgnesSelectItem>
                      ))}
                    </AgnesSelectContent>
                  </AgnesSelect>
                  <RecomendacaoAgnes dataQa="cbo-analise-etapa-recomendacao" />
                </div>
                <div className="mt-6 self-start rounded-lg bg-agnes-bg-muted p-3">
                  <Info
                    label="Status de análise"
                    value={labelOf(STATUS_LABEL, rec.status)}
                    dataQa="cbo-analise-status"
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      <div
        className="mt-6 flex justify-end gap-3 border-t border-agnes-border pt-4"
        data-qa="cbo-analise-rodape"
      >
        <AgnesButton
          variant="text"
          startIcon={<ArrowLeft className="h-4 w-4" />}
          onClick={voltar}
          disabled={salvando}
          dataQa="cbo-analise-voltar"
        >
          Voltar
        </AgnesButton>
        {ultimaAba ? (
          <AgnesButton
            variant="primary"
            startIcon={<Check className="h-4 w-4" />}
            onClick={submit}
            disabled={salvando || carregandoAnalise || !isDirty}
            dataQa="cbo-analise-salvar"
          >
            {salvando ? 'Salvando…' : 'Salvar'}
          </AgnesButton>
        ) : (
          <AgnesButton
            variant="primary"
            endIcon={<ArrowRight className="h-4 w-4" />}
            onClick={avancar}
            disabled={carregandoAnalise}
            dataQa="cbo-analise-confirmar-avancar"
          >
            Confirmar e avançar
          </AgnesButton>
        )}
      </div>
    </div>
  );
}
